import * as React from "react";
import { Field as FormikField } from "formik";
import { lighten } from "polished";
import styled, { css } from "styled-components";

import { sentenceCase } from "src/library/utils";

import Grid from "../Hedron";
import { Tooltip } from "../Tooltip";
import { Text } from "../Text";
import { Icon } from "../Icon";
import { Icons } from "../Icon/types";
import { IFormSelect } from ".";
import FieldError from "./FieldError";
import Label from "./Label";

export const StyledSelectWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: ${theme.spacing.margins.md} 0;
    min-height: 36px;
  `}
`;

const SelectStyled = styled("div")`
  position: relative;
  max-width: 400px;

  &:after {
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    content: "";
    border-top: 8px solid black;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    pointer-events: none;
    opacity: 0.5;
  }

  .select {
    appearance: none;
    box-sizing: border-box;
    padding-left: 14px;
    padding-right: 36px;
    width: 100%;
    min-width: 100px;
    height: 36px;
    color: ${({ theme }) => theme.fonts.colors.primary};
    font-size: ${({ theme }) => theme.fonts.sizes.p};
    line-height: 1em;
    background-color: ${({ theme }) => theme.colors.white};
    border: 2px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: ${({ theme }) => theme.borderRadius};
    outline: none;
    font-family: ${({ theme }) => theme.fonts.family.body || "Roboto"}, Helvetica;

    &:disabled {
      cursor: not-allowed;
      color: ${({ theme }) => lighten(0.3, theme.colors.midGrey)};
    }

    &::-ms-expand {
      display: none;
    }
  }
`;

const FormikSelectStyled = styled(FormikField).attrs({ component: "select" })``;
const OptionStyled = styled.option``;

const Select = React.forwardRef<unknown, IFormSelect>((
  {
    as,
    name,
    autoHideLabels,
    label,
    labelTooltip,
    fieldLogic,
    error,
    handleChange,
    options,
    className,
    value,
    defaultValue: staticDefaultValue,
    disabled,
    unselectedOption,
    validation
  },
  ref
) => {
  const [ defaultValue, setDefaultValue ] = React.useState<string | undefined>(staticDefaultValue);

  React.useEffect(() => {
    // If no defaultValue is set, check if one has been given
    if (!defaultValue) {
      options.map(opt => {
        if (opt.checked) {
          setDefaultValue(opt.value);
        }
      });
    }
  }, [ defaultValue, options ]);

  return (
    <>
      {!autoHideLabels && label && (
        <Grid.Box>
          <Grid.Bounds
            direction="horizontal"
            width="100%"
          >
            <Label
              for={name}
              label={label}
              required={validation?._exclusive?.required}
            />

            {labelTooltip && (
              <>
                <div
                  className="help-icon"
                  data-tip
                  data-for={`${name}-tooltip`}
                >
                  <Icon
                    icon={Icons.HELP}
                    size={10}
                  />
                </div>

                <Tooltip
                  id={`${name}-tooltip`}
                  type="dark"
                  place="top"
                  getContent={() => {
                    return (
                      <Text color="white">
                        {labelTooltip}
                      </Text>
                    );
                  }}
                />
              </>
            )}
          </Grid.Bounds>
        </Grid.Box>
      )}

      <Grid.Box className={className}>
        <SelectStyled>
          {/*
          // @ts-ignore */}
          <FormikSelectStyled
            ref={ref}
            className="select"
            // @ts-ignore
            as={as}
            name={name}
            defaultValue={defaultValue}
            value={value}
            onChange={(e: React.ChangeEvent<any>) => {
              const value = e.target.value;

              if (fieldLogic) fieldLogic(value);

              if (handleChange) {
                handleChange(e);
              }
            }}
            disabled={disabled}
          >
            {!unselectedOption ? (
              <OptionStyled
                value=""
                disabled
              >
                Please Select
              </OptionStyled>
            ) : (
              <OptionStyled
                value={unselectedOption.value}
                disabled={unselectedOption.disabled}
              >
                {unselectedOption.text}
              </OptionStyled>
            )}

            {options.map((opt, i) => (
              <OptionStyled
                key={`${name}-${i}`}
                value={opt.value}
                disabled={opt.disabled}
              >
                {opt.text}
              </OptionStyled>
            ))}
          </FormikSelectStyled>
        </SelectStyled>
      </Grid.Box>

      {error && (
        <Grid.Box>
          <FieldError>
            {sentenceCase(error)}
          </FieldError>
        </Grid.Box>
      )}
    </>
  );
});

export default Select;
